import React, { useState, useRef } from 'react';
import { uploadFamilyPhoto } from '../utils/familyImageUpload';
import { PhotoIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';

const FamilyImageUpload = ({ 
  currentImage, 
  onUploadSuccess, 
  familyId, 
  className = '', 
  size = 'w-32 h-32' 
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      await uploadFamilyPhoto({
        file,
        familyId,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
        onError: (errorMessage) => {
          setError(errorMessage);
          setUploading(false);
        },
        onSuccess: (url, imageData) => {
          onUploadSuccess(url, imageData);
          setUploading(false);
          setUploadProgress(0);
        }
      });
    } catch (error) {
      setError(error.message);
      setUploading(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div
        className={`relative ${size} rounded-xl overflow-hidden bg-white group cursor-pointer border-2 border-dashed border-gray-300 hover:border-cerulean transition-colors duration-200`}
        onClick={() => fileInputRef.current?.click()}
      >
        {currentImage ? (
          <img 
            src={currentImage} 
            alt="Family photo" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
            <PhotoIcon className="w-8 h-8 mb-2" />
            <span className="text-xs text-center">Click to upload<br />family photo</span>
          </div>
        )}
        
        {/* Upload overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <div className="text-xs">Uploading... {Math.round(uploadProgress)}%</div>
            </div>
          </div>
        )}
        
        {/* Upload icon overlay */}
        {!uploading && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
            <CloudArrowUpIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </div>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />
      
      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
};

export default FamilyImageUpload;
